import React, { useState, useEffect } from 'react';
import { makeStyles } from 'design-system/styles';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer';
import theme from 'design-system/theme';
import { Select, Slider, Option, Switch } from 'react-scale/components/design-system';
import cx from 'classnames';
import { useLocalStorage } from 'react-use-v13';
import SanitizedMarkdown from 'react-scale/components/design-system/SanitizedMarkdown';

interface PairwiseEvaluationProps {
  content: string;
  left: string;
  right: string;
}

enum DiffMethodOptions {
  Raw = 'Raw Text',
  NoHighlight = 'No Highlight',
  Words = 'Words',
  Sentences = 'Sentences',
}

const localStorageFontSizeKey = 'pairwiseEvaluation.fontSize';
const localStorageDiffMethodKey = 'pairwiseEvaluation.diffMethod';
const localStorageStripNewlinesKey = 'pairwiseEvaluation.stripNewlines';
const localStorageShowMarkdownKey = 'pairwiseEvaluation.showMarkdown';

const PairwiseEvaluation: React.FC<PairwiseEvaluationProps> = ({ content, left, right }) => {
  const classes = useStyles();
  const [fontSize, setFontSize] = useLocalStorage<number>(localStorageFontSizeKey, 15);
  const [diffMethod, setDiffMethod] = useLocalStorage<DiffMethodOptions>(
    localStorageDiffMethodKey,
    DiffMethodOptions.NoHighlight,
  );
  const [stripNewlines, setStripNewlines] = useLocalStorage<boolean>(
    localStorageStripNewlinesKey,
    false,
  );
  const [showMarkdown, setShowMarkdown] = useLocalStorage<boolean>(
    localStorageShowMarkdownKey,
    false,
  );
  const [leftText, setLeftText] = useState(left);
  const [rightText, setRightText] = useState(right);

  useEffect(() => {
    if (stripNewlines || diffMethod === DiffMethodOptions.Raw) {
      // for raw text comparison, always strip newlines because otherwise diff viewer can misalign lines
      setLeftText(left.replaceAll(/\n+/g, ' '));
      setRightText(right.replaceAll(/\n+/g, ' '));
    } else {
      setLeftText(left);
      setRightText(right);
    }
  }, [stripNewlines, left, right, diffMethod]);

  const renderDiffViewer = () => {
    if (showMarkdown) {
      return (
        <div className={classes.markdownContainer}>
          <div className={classes.pairwiseHalf}>
            <div className={classes.titleBlock}>Left</div>
            <div className={classes.markdownBlock}>
              <SanitizedMarkdown text={leftText} />
            </div>
          </div>
          <div className={classes.pairwiseHalf}>
            <div className={classes.titleBlock}>Right</div>
            <div className={classes.markdownBlock}>
              <SanitizedMarkdown text={rightText} />
            </div>
          </div>
        </div>
      );
    } else if (diffMethod === DiffMethodOptions.Raw) {
      // use diff viewer but override background
      return (
        <ReactDiffViewer
          oldValue={leftText}
          newValue={rightText}
          splitView
          hideLineNumbers
          showDiffOnly={false} // don't collapse unchanged text
          leftTitle="Left"
          rightTitle="Right"
          disableWordDiff
          styles={{
            contentText: { fontFamily: theme.typography.fontFamily, fontSize },
            variables: { light: { removedBackground: 'white', addedBackground: 'white' } },
          }}
        />
      );
    } else {
      return (
        <ReactDiffViewer
          oldValue={leftText}
          newValue={rightText}
          compareMethod={
            diffMethod === DiffMethodOptions.Words
              ? DiffMethod.CSS
              : diffMethod === DiffMethodOptions.Sentences
              ? DiffMethod.SENTENCES
              : undefined
          }
          splitView
          hideLineNumbers
          showDiffOnly={false} // don't collapse unchanged text
          leftTitle="Left"
          rightTitle="Right"
          disableWordDiff={
            ![DiffMethodOptions.Words, DiffMethodOptions.Sentences].includes(diffMethod)
          }
          styles={{
            contentText: { fontFamily: theme.typography.fontFamily, fontSize },
            variables: {
              light: {
                // blues to mitigate bias
                removedBackground: '#eef4ff',
                wordRemovedBackground: '#b8c6fc',
                removedGutterBackground: '#dce7fc',
              },
            },
          }}
        />
      );
    }
  };

  return (
    <div className={classes.background}>
      <div className={cx(classes.row, classes.header)}>
        <h3 className={classes.title}>{content}</h3>
        <div className={classes.right}>
          <div className={classes.row}>
            <p className={classes.label}>Diff Method</p>
            <div className={classes.select}>
              <Select
                onChange={(e: React.ChangeEvent<{ value: DiffMethodOptions }>) =>
                  setDiffMethod(e.target.value)
                }
                value={diffMethod}
              >
                {Object.values(DiffMethodOptions).map(option => (
                  <Option value={option} key={option}>
                    {option}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
          <div className={classes.row}>
            <p>Font Size</p>
            <Slider
              className={classes.slider}
              position={fontSize as any}
              minVal={10}
              maxVal={40}
              onAdjust={(n: number) => setFontSize(n)}
            />
          </div>
          <div className={classes.row}>
            <p className={classes.label}>Strip Newlines</p>
            <Switch checked={stripNewlines} onChange={() => setStripNewlines(!stripNewlines)} />
          </div>
          <div className={classes.row}>
            <p className={classes.label}>Show Markdown</p>
            <Switch checked={showMarkdown} onChange={() => setShowMarkdown(!showMarkdown)} />
          </div>
        </div>
      </div>
      {renderDiffViewer()}
    </div>
  );
};

const useStyles = makeStyles({
  background: {
    backgroundColor: 'white',
    width: '100%',
    padding: 20,
  },
  title: {
    width: '80%',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  slider: {
    width: '60%',
    marginLeft: 10,
  },
  right: {
    marginLeft: 'auto',
  },
  label: {
    marginRight: 20,
  },
  rawText: {
    margin: 20,
  },
  select: {
    marginLeft: -10,
  },
  header: {
    position: 'sticky',
    top: 0,
    backgroundColor: 'white',
    borderBottom: '1px solid lightgrey',
    zIndex: 1,
  },
  markdownContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  pairwiseHalf: {
    width: '50%',
  },
  titleBlock: {
    background: '#fafbfc',
    padding: 10,
    borderBottom: '1px solid #eee',
    borderLeft: '1px solid #eee',
  },
  markdownBlock: {
    padding: 10,
    border: '1px solid #eee',
  },
});

export default PairwiseEvaluation;
